import { Box, Paper, Skeleton, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip } from "@mui/material";
import React, { useState } from "react";

interface ColumnNameConfig<T> {
  header: string;
  field: keyof T; 
  align?: "left" | "right" | "center";
  format?: (val: T[keyof T]) => string | number;
  csvFormat?: (val: T[keyof T]) => string;
  width?: string | number;
  tooltip?: string;
  key?: string; // Optional unique identifier for the column
  relatedToKeys?: string[]; // Optional: which keys this column is related to for highlighting
}

interface PlotDataTableProps<T> {
  data: T[];
  columns: ColumnNameConfig<T>[];
  isLoading?: boolean;
  sx?: SxProps;
  highlightColumnKey?: string; // Key of the column to highlight
}

const PlotDataTable = <T,>({ data, columns, isLoading, sx, highlightColumnKey }: PlotDataTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };



  return (
    <>
      <TableContainer
        className="plot-table-view"
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: "white",
          pb: 0,
          height: "100%",
          overflowX: "auto",
          flexGrow: 1
        }}
      >
        <Table stickyHeader sx={{ minWidth: "max-content" }}>
          <TableHead className="plot-table-header">
            <TableRow>
              {columns.map((col) => {
                return (
                  <TableCell
                    key={String(col.field)}
                    align={col.align}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.tooltip ? (
                      <Tooltip title={col.tooltip} arrow>
                        <span style={{ whiteSpace: "nowrap" }}>{col.header}</span>
                      </Tooltip>
                    ) : (
                      col.header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage / 2 }).map((_, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell key={String(col.field)} align={col.align} style={col.width ? { width: col.width } : undefined}>
                      <Skeleton animation="wave" variant="text" width="100%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  Keine Daten
                </TableCell>
              </TableRow>
            ) : (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                <TableRow 
                  className="plot-table-row" 
                  key={rowIndex}
                >
                  {columns.map((col) => {
                    const cellValue = row[col.field];
                    const displayValue = col.format ? col.format(cellValue) : cellValue;
                    const isHighlighted = highlightColumnKey && col.key === highlightColumnKey;
                    const isRelatedHighlighted = highlightColumnKey && col.relatedToKeys?.includes(highlightColumnKey);
                    const shouldHighlight = isHighlighted || isRelatedHighlighted;
                    
                    return (
                      <TableCell 
                        key={String(col.field)} 
                        align={col.align} 
                        style={col.width ? { width: col.width } : undefined}
                        sx={shouldHighlight ? { 
                          backgroundColor: '#e5f2feff',
                          fontWeight: 'bold'
                        } : undefined}
                      >
                        {React.isValidElement(displayValue) ? displayValue : String(displayValue)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 30]}
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`}
          sx={{
            fontSize: 11,
            fontFamily: "Inter",
            borderBottom: "none",
            "& .MuiTablePagination-toolbar": {
              fontSize: 11,
              fontFamily: "Inter",
              minHeight: 30,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: 11,
              fontFamily: "Inter",
            },
            "& .MuiInputBase-root": {
              fontSize: 11,
              fontFamily: "Inter",
            },
            "& .MuiTablePagination-select": {
              fontSize: 11,
              fontFamily: "Inter",
              paddingTop: 0.5,
              paddingBottom: 0.5,
            },
            "& svg": {
              fontSize: 18,
            },
          }}
          slotProps={{
            select: {
              MenuProps: {
                MenuListProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: 11,
                      fontFamily: "Inter",
                    },
                  },
                },
              },
            },
          }}
        />
      </Box>
    </>
  );
};

export default PlotDataTable;
